import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import QuoteModal from "@/components/QuoteModal";

const livoniaRedOak = "/images/site/red-oak-refinished-floor-dining-room-livonia-mi.webp";
import vikingBirmingham from "@/assets/viking-european-white-oak-birmingham-dining.webp";
const annArborMaple = "/images/projects/maple-michigan-plank-site-finished-floor-living-room-ann-arbor-mi.jpg";
import harborSprings from "@/assets/harbor-springs-maple-great-room.webp";

const hubs = [
  {
    title: "Livonia, Northville & Plymouth",
    subtitle: "Extensive project history in Livonia, Northville, Plymouth and surrounding communities",
    slug: "northville",
    to: "/#showroom",
    neighbors: true,
    image: livoniaRedOak,
    alt: "Dining room with a refinished red oak floor, Livonia, MI",
    caption: "Pictured: a 1 Wood Floors project in Livonia, MI",
    span: "col-span-2 row-span-2",
    cta: "Service for Livonia, Northville & Plymouth",
  },
  {
    title: "Birmingham, Bloomfield & Troy",
    subtitle: "Extensive project history in Birmingham, Bloomfield, Troy and surrounding communities",
    slug: "birmingham",
    image: vikingBirmingham,
    alt: "Dining room with a ¾ inch engineered Viking European white oak floor in Chisholm, Birmingham, MI",
    caption: "Pictured: a 1 Wood Floors project in Birmingham, MI",
    span: "col-span-2 row-span-1",
    cta: "Service for Birmingham, Bloomfield & Troy",
  },
  {
    title: "Ann Arbor, Dexter & Brighton",
    subtitle: "Extensive project history in Ann Arbor, Dexter, Brighton and surrounding communities",
    slug: "ann-arbor",
    image: annArborMaple,
    alt: "Living room with a 3¼ inch Michigan Plank first grade maple floor, site finished, Ann Arbor, MI",
    caption: "Pictured: a 1 Wood Floors project in Ann Arbor, MI",
    span: "col-span-1 row-span-1",
    cta: "Service for Ann Arbor, Dexter & Brighton",
  },
  {
    title: "Petoskey, Harbor Springs & Traverse City",
    subtitle: "Extensive project history in Petoskey, Harbor Springs, Traverse City and surrounding communities",
    slug: "harbor-springs",
    image: harborSprings,
    alt: "Great room with a maple floor in an invisible finish overlooking Little Traverse Bay, Harbor Springs, MI",
    caption: "Pictured: a 1 Wood Floors project in Harbor Springs, MI",
    span: "col-span-1 row-span-1",
    cta: "Service for Petoskey, Harbor Springs & Traverse City",
  },
];

const ServiceAreasSection = () => (
  <section className="bg-background py-14 md:py-20 lg:py-28" id="areas">
    <div className="max-w-[1200px] mx-auto px-5 md:px-12">
      {/* Section header */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mb-8 md:mb-12"
      >
        <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-2">
          Where We Work
        </p>
        <h2 className="font-display text-[clamp(28px,5vw,52px)] font-semibold text-foreground leading-[1.1]">
          Where we <em className="italic font-normal text-primary">call home</em>
        </h2>
      </motion.div>

      {/* Seamless Mosaic Grid */}
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.1 }}
        className="rounded-[28px] overflow-hidden"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-[4px]" style={{ gridAutoRows: "minmax(180px, 1fr)" }}>
          {hubs.map((hub) => (
            <div key={hub.slug} className={`${hub.span} flex flex-col min-w-0`}>
            <Link
              to={hub.to ?? `/hardwood-flooring/${hub.slug}-mi`}
              className="relative block flex-1 min-h-[180px] group overflow-hidden"
            >
              <img width={800} height={534} loading="lazy"
                src={hub.image}
                alt={hub.alt}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 grayscale-[20%] contrast-[1.1]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
              <div className="relative z-10 flex flex-col justify-end h-full p-5 md:p-7">
                <h3 className="font-display text-lg md:text-2xl font-semibold text-white leading-tight mb-1.5">
                  {hub.title}
                </h3>
                <p className="text-[11px] md:text-xs font-light text-white/80 leading-relaxed">
                  {hub.subtitle}
                </p>
                {hub.cta && (
                  <span className="mt-3 font-display text-[13px] md:text-sm text-white/60 group-hover:text-white/90 transition-colors duration-300 tracking-wide">
                    {hub.cta} <span className="inline-block translate-x-0 group-hover:translate-x-1 transition-transform duration-300">→</span>
                  </span>
                )}
              </div>
            </Link>
            {hub.neighbors && (
              <p className="text-xs text-muted-foreground bg-background text-center px-3 py-2 shrink-0">
                We also serve neighboring <Link to="/hardwood-flooring/plymouth-mi" className="text-foreground/80 underline-offset-4 hover:underline transition-colors">Plymouth</Link>, <Link to="/hardwood-flooring/canton-mi" className="text-foreground/80 underline-offset-4 hover:underline transition-colors">Canton</Link> and <Link to="/hardwood-flooring/northville-mi" className="text-foreground/80 underline-offset-4 hover:underline transition-colors">Northville</Link>.
              </p>
            )}
            <p className="text-xs text-muted-foreground bg-background text-center px-3 py-2 shrink-0">{hub.caption}</p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Extended Service CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
        className="mt-8 md:mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5"
      >
        <div className="max-w-xl">
          <p className="font-display text-lg md:text-xl font-semibold text-foreground mb-1">
            Serving Beyond Our Hubs
          </p>
          <p className="text-sm font-light text-muted-foreground leading-relaxed">
            We frequently deploy senior crews nationwide for high-value residential and commercial projects
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 shrink-0">
          <QuoteModal
            trigger={
              <button className="inline-flex items-center gap-2 bg-foreground text-primary-foreground px-6 py-3 rounded-full text-sm font-medium hover:bg-primary transition-colors">
                Inquire About Your Area <ArrowRight className="w-4 h-4" />
              </button>
            }
          />
          <a
            href="tel:2483258853"
            className="border border-border text-foreground px-6 py-3 rounded-full text-sm font-medium hover:bg-neutral-50 transition-colors text-center"
          >
            (248) 325-8853
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);

export default ServiceAreasSection;
